import { useLocation } from "@tanstack/react-router"
import { IconCopyIdOutline18 as CopyIdIcon } from "nucleo-ui-outline-18"

import type { SessionData } from "@productlane/auth"
import { Button } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"

import { toast } from "@/components/toast"
import { BAR_BUTTON, BAR_ICON } from "./bar-styles"
import {
	buildDebugMarkdown,
	copyThreadCommand,
	parsePageContext,
	roleLabel,
} from "./impersonation-context"

const ENTITY_LABELS = {
	thread: "Thread ID",
	contact: "Contact ID",
	company: "Company ID",
	issue: "Issue ID",
	project: "Project ID",
} as const

const shortId = (id: string) =>
	id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id

async function copy(value: string, label: string) {
	await navigator.clipboard.writeText(value)
	toast.success({ title: `${label} copied`, icon: CopyIdIcon })
}

export function ImpersonationCopyMenu({
	session,
	plan,
	stripeCustomerId,
}: {
	session: SessionData
	plan?: string | null
	stripeCustomerId?: string | null
}) {
	const location = useLocation()
	const page = parsePageContext(location.pathname)

	const rows: Array<{ label: string; value: string }> = [
		{ label: "User ID", value: session.userId },
		{ label: "Workspace ID", value: session.workspaceId },
	]
	if (page) rows.push({ label: ENTITY_LABELS[page.entity], value: page.id })

	const markdown = buildDebugMarkdown({
		workspaceName: session.workspaceName,
		workspaceId: session.workspaceId,
		plan,
		userName: session.name,
		email: session.email,
		role: roleLabel(session.membershipRole),
		userId: session.userId,
		pathname: location.pathname,
		stripeCustomerId,
	})

	return (
		<DropdownMenu
			contentProps={{ side: "top", align: "end", sideOffset: 8 }}
			className="w-72"
			trigger={
				<Button
					variant="ghost"
					size="sm"
					Icon={CopyIdIcon}
					iconClassName={BAR_ICON}
					className={BAR_BUTTON}
				>
					Copy IDs
				</Button>
			}
		>
			<DropdownMenuLabel>Copy</DropdownMenuLabel>
			{rows.map((row) => (
				<DropdownMenuItem
					key={row.label}
					onSelect={() => void copy(row.value, row.label)}
				>
					<span className="flex-1">{row.label}</span>
					<span className="font-mono text-xs font-normal text-tertiary">
						{shortId(row.value)}
					</span>
				</DropdownMenuItem>
			))}
			{page?.entity === "thread" ? (
				<DropdownMenuItem
					onSelect={() =>
						void copy(copyThreadCommand(page.id), "copy-thread command")
					}
				>
					<span className="flex-1">copy-thread command</span>
				</DropdownMenuItem>
			) : null}
			<DropdownMenuSeparator />
			<DropdownMenuItem onSelect={() => void copy(markdown, "Debug block")}>
				<span className="flex-1">Everything as Markdown</span>
			</DropdownMenuItem>
		</DropdownMenu>
	)
}
